'use client'

import { useState, useRef, useEffect } from 'react'
import Header from '@/components/header'
import { Upload, Download, Eye, FileText, Image as ImageIcon, X, ArrowLeft } from 'lucide-react'
import { useRouter } from 'next/navigation'

// 动态导入 PDF.js 类型
type PDFJSLib = typeof import('pdfjs-dist')

interface ExtractedImage {
  url: string
  name: string
  size: number
  width: number
  height: number
  blob: Blob
}

export default function PDFToolPage() {
  const router = useRouter()
  const [extractedImages, setExtractedImages] = useState<ExtractedImage[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [progressText, setProgressText] = useState('')
  const [isDragOver, setIsDragOver] = useState(false)
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [pdfJsLoaded, setPdfJsLoaded] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pdfjsLibRef = useRef<PDFJSLib | null>(null)

  // 初始化 PDF.js worker (仅在客户端加载)
  useEffect(() => {
    const loadPdfJs = async () => {
      const pdfjsLib = await import('pdfjs-dist')
      pdfjsLibRef.current = pdfjsLib
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/nextjs/pdf-worker/pdf.worker.min.mjs'
      setPdfJsLoaded(true)
    }
    loadPdfJs()
  }, [])

  // 处理文件选择
  const handleFileSelect = (file: File) => {
    if (file && file.type === 'application/pdf') {
      handleFile(file)
    }
  }

  // 处理PDF文件
  const handleFile = async (file: File) => {
    if (!pdfJsLoaded) {
      alert('PDF.js 库正在加载中，请稍候...')
      return
    }

    setExtractedImages([])
    setIsProcessing(true)
    setProgress(0)

    try {
      const arrayBuffer = await file.arrayBuffer()
      await extractImagesFromPDF(arrayBuffer, file.name)
      setIsProcessing(false)
    } catch (error: any) {
      console.error('处理 PDF 失败:', error)
      setProgressText('处理失败: ' + error.message)
      setIsProcessing(false)
    }
  }

  // 从PDF提取图片
  const extractImagesFromPDF = async (arrayBuffer: ArrayBuffer, fileName: string) => {
    if (!pdfjsLibRef.current) return
    const pdfjsLib = pdfjsLibRef.current
    const images: ExtractedImage[] = []

    const pdfDocument = await pdfjsLib.getDocument({
      data: arrayBuffer,
      useSystemFonts: true,
      disableFontFace: false,
      verbosity: 0,
      isEvalSupported: false,
      maxImageSize: 1024 * 1024 * 10,
    }).promise

    const totalPages = pdfDocument.numPages
    let imageIndex = 0

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      updateProgress(pageNum, totalPages)

      const page = await pdfDocument.getPage(pageNum)
      const operatorList = await page.getOperatorList()

      for (let i = 0; i < operatorList.fnArray.length; i++) {
        const fn = operatorList.fnArray[i]

        if (
          fn === pdfjsLib.OPS.paintImageXObject ||
          fn === pdfjsLib.OPS.paintInlineImageXObject
        ) {
          const imageName = operatorList.argsArray[i][0]

          await new Promise<void>((resolve) => {
            page.objs.get(imageName, async (img: any) => {
              if (!img) {
                resolve()
                return
              }

              try {
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')
                if (!ctx) {
                  resolve()
                  return
                }

                // 处理不同类型的图片对象
                if (img.bitmap && img.bitmap instanceof ImageBitmap) {
                  canvas.width = img.width
                  canvas.height = img.height
                  ctx.drawImage(img.bitmap, 0, 0)
                  await finishImageProcessing(canvas, fileName, pageNum, imageIndex, images)
                } else if (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap) {
                  canvas.width = img.width
                  canvas.height = img.height
                  ctx.drawImage(img, 0, 0)
                  await finishImageProcessing(canvas, fileName, pageNum, imageIndex, images)
                } else if (img.data && img.width && img.height) {
                  canvas.width = img.width
                  canvas.height = img.height
                  const imageData = ctx.createImageData(img.width, img.height)
                  imageData.data.set(img.data)
                  ctx.putImageData(imageData, 0, 0)
                  await finishImageProcessing(canvas, fileName, pageNum, imageIndex, images)
                } else if (
                  img instanceof HTMLImageElement ||
                  img instanceof HTMLCanvasElement
                ) {
                  canvas.width = img.width
                  canvas.height = img.height
                  ctx.drawImage(img, 0, 0)
                  await finishImageProcessing(canvas, fileName, pageNum, imageIndex, images)
                }

                resolve()
              } catch (error) {
                console.error('处理图片失败:', error)
                resolve()
              }
            })
          })

          imageIndex++
        }
      }
    }

    setExtractedImages(images)
  }

  // 完成图片处理
  const finishImageProcessing = (
    canvas: HTMLCanvasElement,
    fileName: string,
    pageNum: number,
    imageIndex: number,
    images: ExtractedImage[]
  ): Promise<void> => {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob)
          const name = `${fileName.replace('.pdf', '')}_page${pageNum}_img${imageIndex}.png`

          images.push({
            url,
            name,
            size: blob.size,
            width: canvas.width,
            height: canvas.height,
            blob,
          })
        }
        resolve()
      }, 'image/png')
    })
  }

  // 更新进度
  const updateProgress = (current: number, total: number) => {
    const percent = Math.round((current / total) * 100)
    setProgress(percent)
    setProgressText(`正在处理第 ${current}/${total} 页...`)
  }

  // 格式化文件大小
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  // 下载图片
  const downloadImage = (image: ExtractedImage) => {
    const link = document.createElement('a')
    link.href = image.url
    link.download = image.name
    link.click()
  }

  // 拖拽事件处理
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  return (
    <>
      <Header />

      <main className="min-h-screen bg-linear-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse-slow" />
          <div
            className="absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse-slow"
            style={{ animationDelay: '2s' }}
          />
        </div>

        {/* 内容区域 */}
        <div className="relative max-w-7xl mx-auto px-4 py-12">
          {/* 返回按钮 - 固定在左上角 */}
          <button
            onClick={() => router.push('/light')}
            className="group top24 left24 z-40 flex items-center gap-2 px-4 py-3 rounded-2xl bg-card/80 backdrop-blur-xl border-2 border-border hover:border-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 animate-fade-in"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-md scale-0 group-hover:scale-150 transition-transform duration-500" />
              <ArrowLeft className="relative w-5 h-5 text-primary group-hover:text-primary transition-all duration-300 group-hover:-translate-x-1" />
            </div>
            <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors duration-300">
              返回
            </span>
          </button>

          {/* 页面标题 */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
              <FileText className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">PDF 工具</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              PDF 图片
              <span className="bg-linear-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                {' '}
                提取工具{' '}
              </span>
            </h1>
            <p className="text-lg text-muted-foreground">
              上传 PDF 文件，自动提取其中的所有图片
            </p>
          </div>

          {/* 上传区域 */}
          <div className="bg-card rounded-3xl p-8 shadow-xl border border-border mb-8 animate-fade-in-up">
            <div
              className={`
                border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer
                transition-all duration-300
                ${
                  isDragOver
                    ? 'border-primary bg-primary/5 scale-105'
                    : 'border-border hover:border-primary/50 hover:bg-muted/30'
                }
              `}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center gap-4">
                <div
                  className={`
                  w-20 h-20 rounded-full flex items-center justify-center
                  transition-all duration-300
                  ${isDragOver ? 'bg-primary scale-110' : 'bg-primary/10'}
                `}
                >
                  <Upload
                    className={`w-10 h-10 ${isDragOver ? 'text-white' : 'text-primary'}`}
                  />
                </div>
                <div>
                  <div className="text-xl font-bold text-foreground mb-2">
                    点击或拖拽 PDF 文件到此处
                  </div>
                  <div className="text-sm text-muted-foreground">支持单个 PDF 文件上传</div>
                </div>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleFileSelect(file)
              }}
            />

            {/* 进度条 */}
            {isProcessing && (
              <div className="mt-6 animate-fade-in">
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-linear-to-r from-primary to-secondary transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="text-center mt-2 text-sm text-muted-foreground">
                  {progressText}
                </div>
              </div>
            )}
          </div>

          {/* 图片展示区域 */}
          {extractedImages.length > 0 && (
            <div className="bg-card rounded-3xl p-8 shadow-xl border border-border animate-fade-in-up">
              {/* 标题 */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                <h2 className="text-2xl font-bold text-foreground">提取的图片</h2>
                <div className="px-4 py-2 rounded-full bg-linear-to-r from-primary to-secondary text-white text-sm font-bold">
                  {extractedImages.length} 张图片
                </div>
              </div>

              {/* 图片网格 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {extractedImages.map((image, index) => (
                  <div
                    key={index}
                    className="group relative border-2 border-border rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary hover:shadow-xl hover:-translate-y-2 animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {/* 图片预览 */}
                    <div className="aspect-square bg-muted flex items-center justify-center overflow-hidden">
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    {/* 图片信息 */}
                    <div className="p-4 bg-card">
                      <div
                        className="text-sm font-semibold text-foreground mb-2 truncate"
                        title={image.name}
                      >
                        {image.name}
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                        <span>
                          {image.width} × {image.height}
                        </span>
                        <span>{formatBytes(image.size)}</span>
                      </div>

                      {/* 操作按钮 */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => setPreviewImage(image.url)}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 text-foreground text-xs font-medium transition-all duration-200 hover:scale-105"
                        >
                          <Eye className="w-3 h-3" />
                          预览
                        </button>
                        <button
                          onClick={() => downloadImage(image)}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white text-xs font-medium transition-all duration-200 hover:scale-105"
                        >
                          <Download className="w-3 h-3" />
                          下载
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 空状态 */}
          {!isProcessing && extractedImages.length === 0 && (
            <div className="bg-card rounded-3xl p-16 shadow-xl border border-border text-center animate-fade-in-up">
              <div className="text-6xl mb-4 opacity-50">
                <ImageIcon className="w-16 h-16 mx-auto text-muted-foreground" />
              </div>
              <div className="text-muted-foreground">上传 PDF 文件开始提取图片</div>
            </div>
          )}
        </div>
      </main>

      {/* 图片预览模态框 */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-7xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white hover:bg-gray-100 flex items-center justify-center transition-all duration-200 hover:rotate-90"
            >
              <X className="w-6 h-6 text-gray-900" />
            </button>
            <img
              src={previewImage}
              alt="预览"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* 自定义动画样式 */}
      <style jsx global>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out;
          animation-fill-mode: both;
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </>
  )
}
